import React, { createContext, useContext, useEffect, forwardRef, useState, useLayoutEffect, useRef, useReducer, useMemo } from 'react';
import { EditorView } from 'prosemirror-view';
export { EditorView } from 'prosemirror-view';
import { EditorState, Selection, NodeSelection, TextSelection, PluginKey as PluginKey$1, Plugin } from 'prosemirror-state';
export { EditorState } from 'prosemirror-state';
import applyDevTools from 'prosemirror-dev-tools';
import { Node, Fragment, Schema } from 'prosemirror-model';
import { createPortal } from 'react-dom';
import { history } from 'prosemirror-history';
import { keymap } from 'prosemirror-keymap';
import { baseKeymap, toggleMark } from 'prosemirror-commands';
import { canSplit } from 'prosemirror-transform';
import styled from 'styled-components';
import { findSelectedNodeOfType, findParentNodeOfType } from 'prosemirror-utils';
import { MdFormatBold, MdFormatItalic } from 'react-icons/md';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise */

var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p]; };
    return extendStatics(d, b);
};

function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}

var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

function __makeTemplateObject(cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
}

function parseRawValue(value, schema) {
    var parsedNode;
    if (typeof value === 'string') {
        try {
            parsedNode = JSON.parse(value);
        }
        catch (err) {
            // eslint-disable-next-line no-console
            console.error("Error processing value: " + value + " isn't a valid JSON");
            return;
        }
    }
    else {
        parsedNode = value;
    }
    try {
        // ProseMirror always require a child under doc
        if (parsedNode.type === 'doc') {
            if (Array.isArray(parsedNode.content) && parsedNode.content.length === 0) {
                parsedNode.content.push({
                    type: 'paragraph',
                    content: [],
                });
            }
            // Just making sure doc is always valid
            if (!parsedNode.version) {
                parsedNode.version = 1;
            }
        }
        var parsedDoc = Node.fromJSON(schema, parsedNode);
        // throws an error if the document is invalid
        parsedDoc.check();
        return parsedDoc;
    }
    catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error processing document:\n" + err.message + "\n\n", JSON.stringify(parsedNode));
        return;
    }
}

var EditorViewProvider = /** @class */ (function () {
    function EditorViewProvider() {
    }
    EditorViewProvider.prototype.init = function (view) {
        this._editorView = view;
    };
    Object.defineProperty(EditorViewProvider.prototype, "editorView", {
        get: function () {
            if (!this._editorView) {
                throw Error('EditorViewProvider editorView accessed without editorView instance');
            }
            return this._editorView;
        },
        enumerable: false,
        configurable: true
    });
    EditorViewProvider.prototype.getMarks = function () {
        return this.editorView.state.schema.marks;
    };
    EditorViewProvider.prototype.getNodes = function () {
        return this.editorView.state.schema.nodes;
    };
    EditorViewProvider.prototype.execCommand = function (cmd) {
        cmd(this.editorView.state, this.editorView.dispatch);
        this.focus();
    };
    EditorViewProvider.prototype.focus = function () {
        if (!this._editorView || this._editorView.hasFocus()) {
            return false;
        }
        this._editorView.focus();
        this._editorView.dispatch(this._editorView.state.tr.scrollIntoView());
        return true;
    };
    EditorViewProvider.prototype.blur = function () {
        if (!this._editorView || !this._editorView.hasFocus()) {
            return false;
        }
        this._editorView.dom.blur();
        return true;
    };
    EditorViewProvider.prototype.stateToJSON = function () {
        var state = this.editorView.state.toJSON();
        return __assign(__assign({}, state), { plugins: [] });
    };
    EditorViewProvider.prototype.replaceState = function (rawValue) {
        var state = EditorState.fromJSON({
            schema: this.editorView.state.schema,
            plugins: this.editorView.state.plugins,
        }, rawValue);
        this.editorView.updateState(state);
        // Fire an empty transaction to trigger PortalProvider to flush the created nodeViews
        var tr = this.editorView.state.tr;
        this.editorView.dispatch(tr);
    };
    EditorViewProvider.prototype.replaceDocument = function (rawValue, shouldScrollToBottom, shouldAddToHistory) {
        if (shouldScrollToBottom === void 0) { shouldScrollToBottom = true; }
        if (shouldAddToHistory === void 0) { shouldAddToHistory = true; }
        if (!this.editorView || rawValue === undefined || rawValue === null) {
            return false;
        }
        var state = this.editorView.state;
        var schema = state.schema;
        var content = parseRawValue(rawValue, schema);
        if (!content) {
            return false;
        }
        // In case of replacing a whole document, we only need a content of a top level node e.g. document.
        var tr = state.tr.replaceWith(0, state.doc.nodeSize - 2, content.content);
        if (!shouldScrollToBottom && !tr.selectionSet) {
            // Restore selection at start of document instead of the end.
            tr.setSelection(Selection.atStart(tr.doc));
        }
        if (shouldScrollToBottom) {
            tr = tr.scrollIntoView();
        }
        if (!shouldAddToHistory) {
            tr.setMeta('addToHistory', false);
        }
        this.editorView.dispatch(tr);
        return true;
    };
    return EditorViewProvider;
}());

// https://bitbucket.org/atlassian/atlassian-frontend-mirror/src/master/editor/editor-core/src/event-dispatcher/index.ts
var EventDispatcher = /** @class */ (function () {
    function EventDispatcher() {
        this.listeners = {};
    }
    EventDispatcher.prototype.on = function (event, cb) {
        if (!this.listeners[event]) {
            this.listeners[event] = new Set();
        }
        this.listeners[event].add(cb);
    };
    EventDispatcher.prototype.off = function (event, cb) {
        if (!this.listeners[event]) {
            return;
        }
        if (this.listeners[event].has(cb)) {
            this.listeners[event].delete(cb);
        }
    };
    EventDispatcher.prototype.emit = function (event, data) {
        if (!this.listeners[event]) {
            return;
        }
        this.listeners[event].forEach(function (cb) { return cb(data); });
    };
    EventDispatcher.prototype.destroy = function () {
        this.listeners = {};
    };
    return EventDispatcher;
}());

var PluginsProvider = /** @class */ (function () {
    function PluginsProvider() {
        this.dispatcher = new EventDispatcher();
    }
    PluginsProvider.prototype.publish = function (pluginKey, nextPluginState) {
        this.dispatcher.emit(pluginKey.name, nextPluginState);
    };
    PluginsProvider.prototype.subscribe = function (pluginKey, cb) {
        this.dispatcher.on(pluginKey.name, cb);
    };
    PluginsProvider.prototype.unsubscribe = function (pluginKey, cb) {
        this.dispatcher.off(pluginKey.name, cb);
    };
    return PluginsProvider;
}());

var PortalProvider = /** @class */ (function () {
    function PortalProvider() {
        this.shouldUpdatePortals = true;
        this.portals = new Map();
        this.pendingUpdatedProps = new Map();
        this.nodeViewListeners = new Map();
    }
    PortalProvider.prototype.render = function (component, container) {
        this.portals.set(container, createPortal(component, container));
        this.shouldUpdatePortals = true;
    };
    PortalProvider.prototype.update = function (container, props) {
        this.pendingUpdatedProps.set(container, props);
    };
    PortalProvider.prototype.remove = function (container) {
        this.portals.delete(container);
        this.shouldUpdatePortals = true;
    };
    PortalProvider.prototype.flush = function () {
        var _this = this;
        Array.from(this.pendingUpdatedProps.entries()).map(function (_a) {
            var container = _a[0], operation = _a[1];
            var set = _this.nodeViewListeners.get(container);
            if (set) {
                set.forEach(function (cb) { return cb(operation.props); });
            }
            _this.pendingUpdatedProps.delete(container);
        });
        if (this.portalRendererCallback && this.shouldUpdatePortals) {
            this.portalRendererCallback(this.portals);
            this.shouldUpdatePortals = false;
        }
    };
    PortalProvider.prototype.subscribe = function (container, cb) {
        var _a;
        var set = (_a = this.nodeViewListeners.get(container)) !== null && _a !== void 0 ? _a : new Set();
        set.add(cb);
        this.nodeViewListeners.set(container, set);
    };
    PortalProvider.prototype.unsubscribe = function (container, cb) {
        var set = this.nodeViewListeners.get(container);
        if (!set)
            return;
        if (set.has(cb)) {
            set.delete(cb);
        }
        if (set.size === 0) {
            this.nodeViewListeners.delete(container);
        }
        else {
            this.nodeViewListeners.set(container, set);
        }
    };
    PortalProvider.prototype.addPortalRendererCallback = function (cb) {
        this.portalRendererCallback = cb;
        // Render the portals immediately
        cb(this.portals);
    };
    return PortalProvider;
}());

var hasRequiredPerformanceAPIs;
function isPerformanceAPIAvailable() {
    if (hasRequiredPerformanceAPIs === undefined) {
        hasRequiredPerformanceAPIs =
            typeof window !== 'undefined' &&
                'performance' in window &&
                [
                    'measure',
                    'clearMeasures',
                    'clearMarks',
                    'getEntriesByName',
                    'getEntriesByType',
                    'now',
                ].every(function (api) { return !!performance[api]; });
    }
    return hasRequiredPerformanceAPIs;
}

var measureMap = new Map();
function startMeasure(measureName) {
    if (!isPerformanceAPIAvailable()) {
        return;
    }
    performance.mark(measureName + "::start");
    measureMap.set(measureName, performance.now());
}
function stopMeasure(measureName, onMeasureComplete) {
    if (!isPerformanceAPIAvailable()) {
        return;
    }
    performance.mark(measureName + "::end");
    var start = onMeasureComplete ? measureMap.get(measureName) : undefined;
    try {
        performance.measure(measureName, measureName + "::start", measureName + "::end");
    }
    catch (error) {
    }
    finally {
        if (onMeasureComplete) {
            var entry = performance.getEntriesByName(measureName).pop();
            if (entry) {
                onMeasureComplete(entry.duration, entry.startTime);
            }
            else if (start) {
                onMeasureComplete(performance.now() - start, start);
            }
        }
        clearMeasure(measureName);
    }
}
function clearMeasure(measureName) {
    if (!isPerformanceAPIAvailable()) {
        return;
    }
    measureMap.delete(measureName);
    performance.clearMarks(measureName + "::start");
    performance.clearMarks(measureName + "::end");
    performance.clearMeasures(measureName);
}

var LOG_LEVEL;
(function (LOG_LEVEL) {
    LOG_LEVEL["debug"] = "debug";
    LOG_LEVEL["info"] = "info";
    LOG_LEVEL["warn"] = "warn";
    LOG_LEVEL["error"] = "error";
})(LOG_LEVEL || (LOG_LEVEL = {}));
var AnalyticsProvider = /** @class */ (function () {
    function AnalyticsProvider(props) {
        var _this = this;
        if (props === void 0) { props = {}; }
        this.shouldTrack = false;
        this.logToConsole = false;
        this.logLevel = LOG_LEVEL.error;
        this.logs = {};
        this.perf = {
            debug: function (namespace, method) {
                if (_this.hasLevel(LOG_LEVEL.debug)) {
                    _this.measure(namespace, method, LOG_LEVEL.debug);
                }
            },
            info: function (namespace, method) {
                if (_this.hasLevel(LOG_LEVEL.info)) {
                    _this.measure(namespace, method, LOG_LEVEL.info);
                }
            },
            warn: function (namespace, method) {
                if (_this.hasLevel(LOG_LEVEL.warn)) {
                    _this.measure(namespace, method, LOG_LEVEL.warn);
                }
            },
            error: function (namespace, method) {
                if (_this.hasLevel(LOG_LEVEL.error)) {
                    _this.measure(namespace, method, LOG_LEVEL.error);
                }
            },
            stop: function (namespace, method, durationThreshold) {
                if (durationThreshold === void 0) { durationThreshold = 0; }
                _this.stopMeasure(namespace, method, durationThreshold);
            }
        };
        var shouldTrack = props.shouldTrack, logToConsole = props.logToConsole, logLevel = props.logLevel;
        if (shouldTrack)
            this.shouldTrack = shouldTrack;
        if (logToConsole)
            this.logToConsole = logToConsole;
        if (logLevel) {
            if (logLevel === 'debug')
                this.logLevel = LOG_LEVEL.debug;
            else if (logLevel === 'info')
                this.logLevel = LOG_LEVEL.info;
            else if (logLevel === 'warn')
                this.logLevel = LOG_LEVEL.warn;
            else if (logLevel === 'error')
                this.logLevel = LOG_LEVEL.error;
        }
    }
    AnalyticsProvider.prototype.hasLevel = function (level) {
        var lvl = this.logLevel;
        switch (level) {
            case 'debug':
                return lvl === 'debug';
            case 'info':
                return lvl === 'debug' || lvl === 'info';
            case 'warn':
                return lvl === 'debug' || lvl === 'info' || lvl === 'warn';
            case 'error':
                return lvl === 'debug' || lvl === 'info' || lvl === 'warn' || lvl === 'error';
            default:
                return false;
        }
    };
    AnalyticsProvider.prototype.startLog = function (namespace, method, level) {
        var _a;
        if (namespace in this.logs) {
            if (method in this.logs[namespace]) {
                this.logs[namespace][method].pending = true;
            }
            else {
                this.logs[namespace][method] = {
                    logs: [],
                    level: level,
                    pending: true
                };
            }
        }
        else {
            this.logs[namespace] = (_a = {},
                _a[method] = {
                    logs: [],
                    level: level,
                    pending: true
                },
                _a);
        }
    };
    AnalyticsProvider.prototype.endLog = function (namespace, method, msg) {
        if (namespace in this.logs) {
            if (method in this.logs[namespace]) {
                this.logs[namespace][method].pending = false;
                this.logs[namespace][method].logs.push(msg);
                var lvl = this.logs[namespace][method].level;
                if (lvl === 'debug')
                    console.debug(msg);
                else if (lvl === 'info')
                    console.info(msg);
                else if (lvl === 'warn')
                    console.warn(msg);
                else if (lvl === 'error')
                    console.error(msg);
            }
            else {
                throw Error("Stopping measurement for namespace '" + namespace + "' with non-existent method: " + method);
            }
        }
        else {
            throw Error("Stopping measurement for non-existent namespace: " + namespace);
        }
    };
    AnalyticsProvider.prototype.getName = function (namespace, method) {
        return namespace + "::" + method;
    };
    AnalyticsProvider.prototype.measure = function (namespace, method, level) {
        if (this.shouldTrack && this.logToConsole) {
            this.startLog(namespace, method, level);
            startMeasure(this.getName(namespace, method));
        }
    };
    AnalyticsProvider.prototype.stopMeasure = function (namespace, method, durationThreshold) {
        var _this = this;
        if (durationThreshold === void 0) { durationThreshold = 0; }
        stopMeasure(this.getName(namespace, method), function (duration, start) {
            if (_this.shouldTrack && _this.logToConsole && durationThreshold <= duration) {
                var msg = _this.getName(namespace, method) + " took " + Math.round(duration) + " ms";
                _this.endLog(namespace, method, msg);
            }
        });
    };
    return AnalyticsProvider;
}());

var EditorContext = createContext({
    portalProvider: new PortalProvider(),
    viewProvider: new EditorViewProvider(),
    pluginsProvider: new PluginsProvider(),
    analyticsProvider: new AnalyticsProvider(),
});
var useEditorContext = function () { return useContext(EditorContext); };

var doc = {
    content: 'block+',
};

var blockquote = {
    content: 'paragraph+',
    group: 'block',
    defining: true,
    selectable: false,
    attrs: {
        class: { default: '' },
    },
    parseDOM: [{ tag: 'blockquote' }],
    toDOM: function () {
        return ['blockquote', 0];
    },
};
var pmBlockquote = {
    content: 'paragraph+',
    group: 'block',
    defining: true,
    selectable: false,
    attrs: {
        class: { default: 'pm-blockquote' },
    },
    parseDOM: [{ tag: 'blockquote' }],
    toDOM: function (node) {
        return ['blockquote', node.attrs, 0];
    },
};

/**
 * NOTE: Need this because TS is too smart and inline everything.
 * So we need to give them separate identity.
 * Probably there's a way to solve it but that will need time and exploration.
 * // http://bit.ly/2raXFX5
 * type T1 = X | Y
 * type T2 = A | T1 | B // T2 = A | X | Y | B
 */
// export type ParagraphWithMarksDefinition =
//   | ParagraphWithAlignmentDefinition
//   | ParagraphWithIndentationDefinition;
var paragraph = {
    content: 'inline*',
    group: 'block',
    selectable: false,
    // marks:
    // 'strong code em link strike subsup textColor underline unsupportedMark unsupportedNodeAttribute',
    parseDOM: [{ tag: 'p' }],
    toDOM: function () {
        return ['p', 0];
    },
};

var text = {
    group: 'inline',
    toDebugString: process.env.NODE_ENV !== 'production' ? undefined : function () { return 'text_node'; },
};

// # What do marks exist?
//
// Marks are categorised into different groups. One motivation for this was to allow the `code` mark
// to exclude other marks, without needing to explicitly name them. Explicit naming requires the
// named mark to exist in the schema. This is undesirable because we want to construct different
// schemas that have different sets of nodes/marks.
//
// Groups provide a level of indirection, and solve this problem. For the immediate use-case, one
// group called "not_code" would have sufficed, but this an ultra-specialised to code.
// Mark group for font styling (e.g. bold, italic, underline, superscript).
var FONT_STYLE = 'fontStyle';

var em = {
    inclusive: true,
    group: FONT_STYLE,
    parseDOM: [{ tag: 'i' }, { tag: 'em' }, { style: 'font-style=italic' }],
    toDOM: function () {
        return ['em'];
    },
};

var strong = {
    inclusive: true,
    group: FONT_STYLE,
    parseDOM: [
        { tag: 'strong' },
        // This works around a Google Docs misbehavior where
        // pasted content will be inexplicably wrapped in `<b>`
        // tags with a font-weight normal.
        {
            tag: 'b',
            getAttrs: function (node) {
                var element = node;
                return element.style.fontWeight !== 'normal' && null;
            },
        },
        {
            tag: 'span',
            getAttrs: function (node) {
                var element = node;
                var fontWeight = element.style.fontWeight;
                return (typeof fontWeight === 'string' &&
                    (fontWeight === 'bold' ||
                        fontWeight === 'bolder' ||
                        /^(bold(er)?|[5-9]\d{2,})$/.test(fontWeight)) &&
                    null);
            },
        },
    ],
    toDOM: function () {
        return ['strong'];
    },
};

function splitBlock(state, dispatch) {
    var _a = state.selection, $from = _a.$from, $to = _a.$to;
    if (state.selection instanceof NodeSelection && state.selection.node.isBlock) {
        if (!$from.parentOffset || !canSplit(state.doc, $from.pos))
            return false;
        if (dispatch)
            dispatch(state.tr.split($from.pos).scrollIntoView());
        return true;
    }
    if (!$from.parent.isBlock)
        return false;
    if (dispatch) {
        var atEnd = $to.parentOffset == $to.parent.content.size;
        var tr = state.tr;
        if (state.selection instanceof TextSelection)
            tr.deleteSelection();
        var deflt = $from.depth == 0 ? undefined : $from.node(-1).contentMatchAt($from.indexAfter(-1)).defaultType;
        var types = atEnd && deflt ? [{ type: deflt }] : undefined;
        var can = canSplit(tr.doc, tr.mapping.map($from.pos), 1, types);
        if (!types && !can && deflt && canSplit(tr.doc, tr.mapping.map($from.pos), 1, deflt && [{ type: deflt }])) {
            types = [{ type: deflt }];
            can = true;
        }
        if (can && deflt) {
            tr.split(tr.mapping.map($from.pos), 1, types);
            var isStuff = !atEnd && !$from.parentOffset && $from.parent.type != deflt;
            var canReplace = $from.node(-1).canReplace($from.index(-1), $from.indexAfter(-1), Fragment.from(deflt.create()));
            if (isStuff && canReplace) {
                tr.setNodeMarkup(tr.mapping.map($from.before()), deflt);
            }
        }
        dispatch(tr.scrollIntoView());
    }
    return true;
}
function createNewPmBlockQuote(state, dispatch) {
    var _a = state.selection, $from = _a.$from, $to = _a.$to;
    var blockquote = state.schema.nodes.pmBlockquote;
    var empty = blockquote.createAndFill();
    var endOfBlock = $from.end();
    if (empty && dispatch) {
        var tr = state.tr.insert(endOfBlock + 1, empty);
        dispatch(tr);
    }
    return false;
}

var PluginKey = /** @class */ (function (_super) {
    __extends(PluginKey, _super);
    function PluginKey(name) {
        var _this = _super.call(this, name) || this;
        _this.name = name;
        return _this;
    }
    return PluginKey;
}(PluginKey$1));

// From https://github.com/PierBover/prosemirror-cookbook
function getActiveMarks(state) {
    var isEmpty = state.selection.empty;
    if (isEmpty) {
        var $from = state.selection.$from;
        var storedMarks = state.storedMarks;
        // Return either the stored marks, or the marks at the cursor position.
        // Stored marks are the marks that are going to be applied to the next input
        // if you dispatched a mark toggle with an empty cursor.
        if (storedMarks) {
            return storedMarks.map(function (mark) { return mark.type.name; });
        }
        else {
            return $from.marks().map(function (mark) { return mark.type.name; });
        }
    }
    else {
        var $head = state.selection.$head;
        var $anchor = state.selection.$anchor;
        // We're using a Set to not get duplicate values
        var activeMarks_1 = new Set();
        // Here we're getting the marks at the head and anchor of the selection
        $head.marks().forEach(function (mark) { return activeMarks_1.add(mark.type.name); });
        $anchor.marks().forEach(function (mark) { return activeMarks_1.add(mark.type.name); });
        return Array.from(activeMarks_1);
    }
}

var basePluginKey = new PluginKey('basePlugin');
function basePluginFactory(portalProvider, pluginsProvider, options) {
    return new Plugin({
        state: {
            init: function (_, state) {
                return {
                    activeNodes: [],
                    activeMarks: [],
                };
            },
            apply: function (tr, pluginState, _oldState, newState) {
                if (tr.docChanged || tr.selectionSet) {
                    var marks = getActiveMarks(newState);
                    var eqMarks = marks.every(function (m) { return pluginState.activeMarks.includes(m); }) && marks.length === pluginState.activeMarks.length;
                    if (!eqMarks) {
                        var nextPluginState = __assign(__assign({}, pluginState), { activeMarks: marks });
                        pluginsProvider.publish(basePluginKey, nextPluginState);
                        return nextPluginState;
                    }
                }
                return pluginState;
            },
        },
        key: basePluginKey,
        props: {},
    });
}

var basePlugin = function (options) { return ({
    name: 'base',
    pmPlugins: function () {
        var plugins = [
            { name: 'history', plugin: function () { return history(); } },
            { name: 'baseKeyMap', plugin: function () { return keymap(baseKeymap); } },
            {
                name: 'otherKeyMap',
                plugin: function () { return keymap({
                    'Ctrl-Alt-p': createNewPmBlockQuote,
                    'Ctrl-Alt-s': splitBlock,
                }); }
            },
            {
                name: 'base',
                plugin: function (_a) {
                    var portalProvider = _a.portalProvider, pluginsProvider = _a.pluginsProvider;
                    return basePluginFactory(portalProvider, pluginsProvider);
                },
            },
        ];
        return plugins;
    },
    nodes: function () {
        return [
            { name: 'doc', node: doc },
            { name: 'paragraph', node: paragraph },
            { name: 'pmBlockquote', node: pmBlockquote },
            { name: 'text', node: text },
        ];
    },
    marks: function () {
        return [
            { name: 'em', mark: em },
            { name: 'strong', mark: strong },
        ];
    }
}); };

function createListenProps(container, portalProvider) {
    return function (cb) {
        useEffect(function () {
            portalProvider.subscribe(container, cb);
            return function () {
                portalProvider.unsubscribe(container, cb);
            };
        }, []);
    };
}

var ReactNodeView = /** @class */ (function () {
    function ReactNodeView(node, view, getPos, portalProvider, pluginsProvider, reactComponentProps, reactComponent) {
        var _this = this;
        /**
         * After the component has been rendered, if this nodeView contains a contentDOM it is
         * appended to the element the component chooses to by forwarding a ref to it.
         * @param node the element the ref points to inside the component.
         */
        this.handleRef = function (node) {
            if (node && _this.contentDOM && !node.contains(_this.contentDOM)) {
                node.appendChild(_this.contentDOM);
            }
        };
        this.node = node;
        this.view = view;
        this.getPos = getPos;
        this.pluginsProvider = pluginsProvider;
        this.portalProvider = portalProvider;
        this.reactComponentProps = reactComponentProps || {};
        this.reactComponent = reactComponent;
    }
    /**
     * This method exists to move initialization logic out of the constructor,
     * so object can be initialized properly before calling render first time.
     *
     * Example:
     * Instance properties get added to an object only after super call in
     * constructor, which leads to some methods being undefined during the
     * first render.
     */
    ReactNodeView.prototype.init = function () {
        // Here, we'll provide a container to render React into.
        // Coincidentally, this is where ProseMirror will put its
        // generated contentDOM. React will throw out that content
        // once rendered, and at the same time we'll append it into
        // the component tree, like a fancy shell game. This isn't
        // obvious to the user, but would it be more obvious on an
        // expensive render?
        //
        this.dom = this.createContainerDOM();
        // Instead of just mindlessly copying the attributes to the container node
        // I think letting the component render them is preferred.
        // You can't really apply styles outside the component to the parent either way
        // and the attributes are stored inside this.node and thus not needed to be actually shown.
        // this.setDomAttrs(this.node, this.dom)
        // @see ED-3790
        // something gets messed up during mutation processing inside of a
        // nodeView if DOM structure has nested plain "div"s, it doesn't see the
        // difference between them and it kills the nodeView
        this.dom.classList.add(this.node.type.name + "__nodeview-dom");
        // Finally, we provide an element to render content into.
        // We will be moving this node around as we need to.
        this.contentDOM = this.createContentDOM();
        if (this.contentDOM) {
            // This contentDOM is thrown out by the reactComponent once it renders
            // but which is then re-added with the handleRef
            this.dom.appendChild(this.contentDOM);
        }
        var useListenProps = createListenProps(this.dom, this.portalProvider);
        this.renderReactComponent(this.render(this.createProps(this.node), this.handleRef, useListenProps));
        return this;
    };
    ReactNodeView.prototype.createProps = function (node) {
        return {
            componentProps: this.reactComponentProps,
            attrs: node.attrs,
        };
    };
    ReactNodeView.prototype.renderReactComponent = function (component) {
        if (!this.dom || !component) {
            return;
        }
        this.portalProvider.render(component, this.dom);
    };
    ReactNodeView.prototype.createContainerDOM = function () {
        return this.node.isInline ? document.createElement('span') : document.createElement('div');
    };
    /**
     * If this.node is a leaf node, there shouldn't be a contentDOM.
     * Perhaps a check should be in place to validate this..
     */
    ReactNodeView.prototype.createContentDOM = function () {
        return undefined;
    };
    ReactNodeView.prototype.render = function (initialProps, forwardRef, useListenProps) {
        return this.reactComponent ? (React.createElement(this.reactComponent, { ref: forwardRef, initialProps: initialProps, useListenProps: useListenProps })) : null;
    };
    ReactNodeView.prototype.update = function (node, _decorations) {
        if (!this.dom)
            return false;
        // Sometimes it might happen that the current transaction transforms/splits a node
        // into entirely different node (eg block nodes becoming paragraphs all of sudden)
        // I guess you could do some magic there to avoid redrawing but for now I have little idea how to
        // utilize it so this returns false and the update is processed by PM default behavior.
        // Might be relevant
        // https://discuss.prosemirror.net/t/how-to-modify-node-attribute-without-replacing-it-and-causing-it-to-re-render/2510/9
        if (node.type !== this.node.type)
            return false;
        this.node = node;
        // TODO pass down also type & marks & decorations if changed?
        this.portalProvider.update(this.dom, this.createProps(node));
        return true;
    };
    /**
     * Copies the attributes from a ProseMirror Node to a DOM node.
     * NOT used at the moment since it messes up classes if used for component HTMLElement directly.
     * Also I think it's better for component to decide how to set those attributes.
     * @param node The Prosemirror Node from which to source the attributes
     */
    ReactNodeView.prototype.setDomAttrs = function (node, element) {
        Object.keys(node.attrs || {}).forEach(function (attr) {
            element.setAttribute(attr, node.attrs[attr]);
        });
    };
    /**
     * Used to prevent changes to DOM inside the contentDOM from confusing PM to attempt
     * "fixing" this nodeView by removing the added children etc
     * https://github.com/remirror/remirror/blob/a2fa2c2b935a6fce99e3f79aad8a207c920e236e/packages/%40remirror/extension-react-component/src/react-node-view.tsx
     * @param mutation
     */
    ReactNodeView.prototype.ignoreMutation = function (mutation) {
        if (mutation.type === 'selection') {
            return false;
        }
        else if (!this.contentDOM) {
            return true;
        }
        return !this.contentDOM.contains(mutation.target);
    };
    ReactNodeView.prototype.destroy = function () {
        if (this.dom) {
            this.portalProvider.remove(this.dom);
        }
        this.dom = undefined;
        this.contentDOM = undefined;
    };
    ReactNodeView.fromComponent = function (component, portalProvider, pluginsProvider, props) {
        return function (node, view, getPos) {
            return new ReactNodeView(node, view, getPos, portalProvider, pluginsProvider, props, component).init();
        };
    };
    return ReactNodeView;
}());

var BlockQuote = forwardRef(function (props, ref) {
    var className = props.className, initialProps = props.initialProps, useListenProps = props.useListenProps;
    useListenProps(handlePropsUpdate);
    var _a = useState(__assign({}, initialProps.attrs)), attrs = _a[0], setAttrs = _a[1];
    function handlePropsUpdate(newProps) {
        // console.log(newProps)
    }
    return (React.createElement(StyledBlockQuote, { className: className, ref: ref }));
});
var StyledBlockQuote = styled.blockquote(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  box-sizing: border-box;\n  color: #6a737d;\n  padding: 0 1em;\n  border-left: 4px solid #dfe2e5;\n  margin: 0.2rem 0 0 0;\n  margin-right: 0;\n\n  [dir='rtl'] & {\n    padding-left: 0;\n    padding-right: 4px;\n  }\n\n  &:first-child {\n    margin-top: 0;\n  }\n\n  &::before {\n    content: '';\n  }\n\n  &::after {\n    content: none;\n  }\n\n  & p {\n    display: block;\n  }\n\n  & table,\n  & table:last-child {\n    display: inline-table;\n  }\n"], ["\n  box-sizing: border-box;\n  color: #6a737d;\n  padding: 0 1em;\n  border-left: 4px solid #dfe2e5;\n  margin: 0.2rem 0 0 0;\n  margin-right: 0;\n\n  [dir='rtl'] & {\n    padding-left: 0;\n    padding-right: 4px;\n  }\n\n  &:first-child {\n    margin-top: 0;\n  }\n\n  &::before {\n    content: '';\n  }\n\n  &::after {\n    content: none;\n  }\n\n  & p {\n    display: block;\n  }\n\n  & table,\n  & table:last-child {\n    display: inline-table;\n  }\n"])));
var templateObject_1;

var BlockQuoteView = /** @class */ (function (_super) {
    __extends(BlockQuoteView, _super);
    function BlockQuoteView() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    BlockQuoteView.prototype.createContentDOM = function () {
        var contentDOM = document.createElement('div');
        contentDOM.classList.add(this.node.type.name + "__content-dom");
        return contentDOM;
    };
    return BlockQuoteView;
}(ReactNodeView));
function blockQuoteNodeView(portalProvider, pluginsProvider, options) {
    return function (node, view, getPos) {
        return new BlockQuoteView(node, view, getPos, portalProvider, pluginsProvider, {
            options: options,
        }, BlockQuote).init();
    };
}

function findBlockQuote(state, selection) {
    var blockquote = state.schema.nodes.blockquote;
    return (findSelectedNodeOfType(blockquote)(selection || state.selection) ||
        findParentNodeOfType(blockquote)(selection || state.selection));
}

var blockquotePluginKey = new PluginKey('blockQuotePlugin');
function blockQuotePluginFactory(portalProvider, pluginsProvider, options) {
    return new Plugin({
        state: {
            init: function (_, state) {
                return {
                    blockQuoteActive: false,
                };
            },
            apply: function (tr, pluginState, _oldState, newState) {
                if (tr.docChanged || tr.selectionSet) {
                    var blockQuoteActive = !!findBlockQuote(newState, newState.selection);
                    // const blockQuoteDisabled = !(
                    //   blockQuoteActive ||
                    //   isWrappingPossible(newState.schema.blockquote, newState)
                    // )
                    if (blockQuoteActive !== pluginState.blockQuoteActive) {
                        var nextPluginState = __assign(__assign({}, pluginState), { blockQuoteActive: blockQuoteActive });
                        pluginsProvider.publish(blockquotePluginKey, nextPluginState);
                        return nextPluginState;
                    }
                }
                return pluginState;
            },
        },
        key: blockquotePluginKey,
        props: {
            nodeViews: {
                blockquote: blockQuoteNodeView(portalProvider, pluginsProvider, options),
            },
        },
    });
}

var result = {
    mac: false,
    ie: false,
    ie_version: 0,
    gecko: false,
    chrome: false,
    chrome_version: 0,
    android: false,
    ios: false,
    webkit: false,
};
if (typeof navigator !== 'undefined') {
    var ieEdge = /Edge\/(\d+)/.exec(navigator.userAgent);
    var ieUpTo10 = /MSIE \d/.test(navigator.userAgent);
    var ie11up = /Trident\/(?:[7-9]|\d{2,})\..*rv:(\d+)/.exec(navigator.userAgent);
    result.mac = /Mac/.test(navigator.platform);
    var ie = (result.ie = !!(ieUpTo10 || ie11up || ieEdge));
    result.ie_version = ieUpTo10
        ? document.documentMode || 6
        : ie11up
            ? +ie11up[1]
            : ieEdge
                ? +ieEdge[1]
                : null;
    result.gecko = !ie && /gecko\/\d/i.test(navigator.userAgent);
    result.chrome = !ie && /Chrome\//.test(navigator.userAgent);
    result.chrome_version = parseInt((navigator.userAgent.match(/Chrome\/(\d{2})/) || [])[1], 10);
    result.android = /Android \d/.test(navigator.userAgent);
    result.ios =
        !ie &&
            /AppleWebKit/.test(navigator.userAgent) &&
            /Mobile\/\w+/.test(navigator.userAgent);
    result.webkit =
        !ie &&
            !!document.documentElement &&
            'WebkitAppearance' in document.documentElement.style;
}

var toggleBold = makeKeyMapWithCommon('Bold', 'Mod-b');
var toggleItalic = makeKeyMapWithCommon('Italic', 'Mod-i');
var toggleUnderline = makeKeyMapWithCommon('Underline', 'Mod-u');
var toggleCode = makeKeyMapWithCommon('Code', 'Mod-Shift-m');
var setNormalText = makeKeyMapWithCommon('Normal text', 'Mod-Alt-0');
var toggleHeading1 = makeKeyMapWithCommon('Heading 1', 'Mod-Alt-1');
var toggleBlockQuote = makeKeyMapWithCommon('Quote', 'Ctrl-Alt-b');
var insertNewLine = makeKeyMapWithCommon('Insert new line', 'Shift-Enter');
var shiftBackspace = makeKeyMapWithCommon('Shift Backspace', 'Shift-Backspace');
var splitCodeBlock = makeKeyMapWithCommon('Split code block', 'Enter');
var undo = makeKeyMapWithCommon('Undo', 'Mod-z');
var moveUp = makeKeyMapWithCommon('Move up', 'ArrowUp');
var moveDown = makeKeyMapWithCommon('Move down', 'ArrowDown');
var moveLeft = makeKeyMapWithCommon('Move left', 'ArrowLeft');
var moveRight = makeKeyMapWithCommon('Move right', 'ArrowRight');
var openHelp = makeKeyMapWithCommon('Open Help', 'Mod-/');
var addLink = makeKeyMapWithCommon('Link', 'Mod-k');
var addInlineComment = makeKeyMapWithCommon('Annotate', 'Mod-Alt-c');
var submit = makeKeyMapWithCommon('Submit Content', 'Mod-Enter');
var enter = makeKeyMapWithCommon('Enter', 'Enter');
var shiftEnter = makeKeyMapWithCommon('Shift Enter', 'Shift-Enter');
var tab = makeKeyMapWithCommon('Tab', 'Tab');
var backspace = makeKeyMapWithCommon('Backspace', 'Backspace');
var deleteKey = makeKeyMapWithCommon('Delete', 'Delete');
var space = makeKeyMapWithCommon('Space', 'Space');
var escape = makeKeyMapWithCommon('Escape', 'Escape');
var nextCell = makeKeyMapWithCommon('Next cell', 'Tab');
var previousCell = makeKeyMapWithCommon('Previous cell', 'Shift-Tab');
var toggleTable = makeKeyMapWithCommon('Table', 'Shift-Alt-t');
var cut = makeKeyMapWithCommon('Cut', 'Mod-x');
var copy = makeKeyMapWithCommon('Copy', 'Mod-c');
var paste = makeKeyMapWithCommon('Paste', 'Mod-v');
var altPaste = makeKeyMapWithCommon('Paste', 'Mod-Shift-v');
var find = makeKeyMapWithCommon('Find', 'Mod-f');
function makeKeymap(description, windows, mac, common) {
    return {
        description: description,
        windows: windows,
        mac: mac,
        common: common,
    };
}
function makeKeyMapWithCommon(description, common) {
    var windows = common.replace(/Mod/i, 'Ctrl');
    var mac = common.replace(/Mod/i, 'Cmd');
    return makeKeymap(description, windows, mac, common);
}
function bindKeymapWithCommand(shortcut, cmd, keymap) {
    var oldCmd = keymap[shortcut];
    var newCmd = cmd;
    if (keymap[shortcut]) {
        newCmd = function (state, dispatch, editorView) {
            return oldCmd(state, dispatch) || cmd(state, dispatch, editorView);
        };
    }
    keymap[shortcut] = newCmd;
}

var createNewBlockQuote = function () { return function (state, dispatch) {
    var _a = state.selection, $from = _a.$from, $to = _a.$to;
    var blockquote = state.schema.nodes.blockquote;
    var empty = blockquote.createAndFill();
    var endOfBlock = $from.end();
    if (empty && dispatch) {
        var tr = state.tr.insert(endOfBlock + 1, empty);
        dispatch(tr);
    }
    return false;
}; };

function keymapPlugin() {
    var keymapObj = {};
    bindKeymapWithCommand(toggleBlockQuote.common, createNewBlockQuote(), keymapObj);
    return keymap(keymapObj);
}

var blockQuotePlugin = function (options) {
    if (options === void 0) { options = {}; }
    return ({
        name: 'blockquote',
        nodes: function () {
            return [{ name: 'blockquote', node: blockquote }];
        },
        pmPlugins: function () {
            var plugins = [
                {
                    name: 'blockquote',
                    plugin: function (_a) {
                        var portalProvider = _a.portalProvider, pluginsProvider = _a.pluginsProvider;
                        return blockQuotePluginFactory(portalProvider, pluginsProvider, options);
                    },
                },
                { name: 'blockquoteKeyMap', plugin: function () { return keymapPlugin(); } },
            ];
            return plugins;
        },
        pluginsOptions: {},
    });
};

var Preset = /** @class */ (function () {
    function Preset() {
        this.plugins = [];
    }
    Preset.prototype.add = function (plugin) {
        this.plugins.push(plugin);
        return this;
    };
    Preset.prototype.has = function (plugin) {
        return this.plugins.some(function (pluginPreset) {
            if (Array.isArray(pluginPreset)) {
                return pluginPreset[0] === plugin;
            }
            return pluginPreset === plugin;
        });
    };
    Preset.prototype.getEditorPlugins = function (excludes) {
        var editorPlugins = this.processEditorPlugins();
        return this.removeExcludedPlugins(editorPlugins, excludes);
    };
    Preset.prototype.processEditorPlugins = function () {
        var cache = new Map();
        this.plugins.forEach(function (pluginEntry) {
            if (Array.isArray(pluginEntry)) {
                var fn = pluginEntry[0], options = pluginEntry[1];
                cache.set(fn, options);
            }
            else {
                /**
                 * Prevent usage of same plugin twice without override.
                 * [
                 *  plugin1,
                 *  [plugin1, { option1: value }],
                 *  plugin1, // This will throw
                 * ]
                 */
                if (cache.has(pluginEntry) && cache.get(pluginEntry) === undefined) {
                    throw new Error(pluginEntry + " is already included!");
                }
                cache.set(pluginEntry, undefined);
            }
        });
        var plugins = [];
        cache.forEach(function (options, fn) {
            plugins.push(fn(options));
        });
        return plugins;
    };
    Preset.prototype.removeExcludedPlugins = function (plugins, excludes) {
        if (excludes) {
            return plugins.filter(function (plugin) { return !plugin || !excludes.has(plugin.name); });
        }
        return plugins;
    };
    return Preset;
}());

function createDefaultEditorPlugins(props, prevProps) {
    var preset = new Preset();
    preset.add(basePlugin);
    preset.add(blockQuotePlugin);
    return preset.getEditorPlugins();
}

var ranks = {
    plugins: [
        'underline',
        'blockquote',
        'quickInsert',
        'typeAhead',
    ],
    nodes: [
        'doc',
        'paragraph',
        'text',
        'bulletList',
        'orderedList',
        'listItem',
        'heading',
        'blockquote',
        'pmBlockquote',
        'codeBlock',
    ],
    marks: [
        // Inline marks
        'link',
        'em',
        'strong',
        'textColor',
        'strike',
        'subsup',
        'underline',
        'code',
        'typeAheadQuery',
        // Block marks
        'alignment',
        'breakout',
        'indentation',
        'annotation',
        //Unsupported mark
        'unsupportedMark',
        'unsupportedNodeAttribute',
    ],
};
function sortByOrder(item) {
    return function (a, b) {
        return ranks[item].indexOf(a.name) - ranks[item].indexOf(b.name);
    };
}

function fixExcludes(marks) {
    var markKeys = Object.keys(marks);
    var markGroups = new Set(markKeys.map(function (mark) { return marks[mark].group; }));
    markKeys.forEach(function (markKey) {
        var mark = marks[markKey];
        if (mark.excludes) {
            mark.excludes = mark.excludes
                .split(' ')
                .filter(function (group) { return markGroups.has(group); })
                .join(' ');
        }
    });
    return marks;
}
function processPluginsList(plugins) {
    /**
     * First pass to collect pluginsOptions
     */
    var pluginsOptions = plugins.reduce(function (acc, plugin) {
        if (plugin.pluginsOptions) {
            Object.keys(plugin.pluginsOptions).forEach(function (pluginName) {
                if (!acc[pluginName]) {
                    acc[pluginName] = [];
                }
                acc[pluginName].push(plugin.pluginsOptions[pluginName]);
            });
        }
        return acc;
    }, {});
    /**
     * Process plugins
     */
    return plugins.reduce(function (acc, plugin) {
        var _a, _b, _c;
        if (plugin.pmPlugins) {
            (_a = acc.pmPlugins).push.apply(_a, plugin.pmPlugins(plugin.name ? pluginsOptions[plugin.name] : undefined));
        }
        if (plugin.nodes) {
            (_b = acc.nodes).push.apply(_b, plugin.nodes());
        }
        if (plugin.marks) {
            (_c = acc.marks).push.apply(_c, plugin.marks());
        }
        return acc;
    }, {
        nodes: [],
        marks: [],
        pmPlugins: [],
    });
}
function createPMPlugins(config) {
    var editorConfig = config.editorConfig, rest = __rest(config, ["editorConfig"]);
    return editorConfig.pmPlugins
        .sort(sortByOrder('plugins'))
        .map(function (_a) {
        var plugin = _a.plugin;
        return plugin(rest);
    })
        .filter(function (plugin) { return typeof plugin !== 'undefined'; });
}

function createSchema(editorConfig) {
    var createdMarks = fixExcludes(editorConfig.marks.sort(sortByOrder('marks')).reduce(function (acc, mark) {
        acc[mark.name] = mark.mark;
        return acc;
    }, {}));
    var createdNodes = editorConfig.nodes.sort(sortByOrder('nodes')).reduce(function (acc, node) {
        acc[node.name] = node.node;
        return acc;
    }, {});
    return new Schema({ nodes: createdNodes, marks: createdMarks });
}

/**
 * Finds all top level nodes affected by the transaction
 * Uses from/to positions in transaction's steps to work out which nodes will
 * be changed by the transaction
 */
var findChangedNodesFromTransaction = function (tr) {
    var nodes = [];
    var steps = (tr.steps || []);
    steps.forEach(function (step) {
        var to = step.to, from = step.from, slice = step.slice;
        var size = slice && slice.content ? slice.content.size : 0;
        var _loop_1 = function (i) {
            if (i <= tr.doc.content.size) {
                var topLevelNode_1 = tr.doc.resolve(i).node(1);
                if (topLevelNode_1 && !nodes.find(function (n) { return n === topLevelNode_1; })) {
                    nodes.push(topLevelNode_1);
                }
            }
        };
        for (var i = from; i <= to + size; i++) {
            _loop_1(i);
        }
    });
    return nodes;
};
var validNode = function (node) {
    try {
        node.check(); // this will throw an error if the node is invalid
    }
    catch (error) {
        return false;
    }
    return true;
};
/** Validates prosemirror nodes, and returns true only if all nodes are valid */
var validateNodes = function (nodes) {
    return nodes.every(validNode);
};
var SelectedState;
(function (SelectedState) {
    SelectedState[SelectedState["selectedInRange"] = 0] = "selectedInRange";
    SelectedState[SelectedState["selectedInside"] = 1] = "selectedInside";
})(SelectedState || (SelectedState = {}));

var getDocStructure = function (doc) {
    try {
        return getBlockNode(doc, 0);
    }
    catch (error) {
        return "Error logging document structure: " + error;
    }
};
var getBlockNode = function (node, pos) {
    var blockNode = {
        type: node.type.name,
        pos: pos,
        nodeSize: node.nodeSize,
    };
    var content = getBlockNodeContent(node.content, pos);
    if (content.length > 0) {
        blockNode.content = content;
    }
    var marks = getMarks(node);
    if (marks.length > 0) {
        blockNode.marks = marks;
    }
    return blockNode;
};
var getBlockNodeContent = function (node, pos) {
    if (!node || !node.content || !node.content.length) {
        return [];
    }
    var blockNodeContent = [];
    var content = node.content;
    if (content[0].isBlock) {
        // children are block nodes
        var prevNode_1;
        blockNodeContent = content.map(function (node) {
            pos += prevNode_1 ? prevNode_1.nodeSize : 1;
            prevNode_1 = node;
            return getBlockNode(node, pos);
        });
    }
    else {
        // children are inline nodes
        var result = getInlineNodes(content, pos);
        blockNodeContent = result.inlineNodes;
        pos = result.pos;
    }
    return blockNodeContent;
};
var getInlineNodes = function (nodes, pos) {
    var inlineNodes = nodes.map(function (node) {
        var nodeSize = node.nodeSize;
        var inlineNode = {
            type: node.type.name,
            pos: pos,
            nodeSize: nodeSize,
        };
        var marks = getMarks(node);
        if (marks.length > 0) {
            inlineNode.marks = marks;
        }
        pos += nodeSize;
        return inlineNode;
    });
    return { inlineNodes: inlineNodes, pos: pos };
};
var getMarks = function (node) {
    return node.marks.map(function (mark) { return mark.type.name; });
};

var useSsrLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

function ReactEditorView(props) {
    var editorProps = props.editorProps, EditorLayoutComponent = props.EditorLayoutComponent;
    var _a = useEditorContext(), viewProvider = _a.viewProvider, pluginsProvider = _a.pluginsProvider, portalProvider = _a.portalProvider, analyticsProvider = _a.analyticsProvider;
    var editorViewRef = useRef(null);
    var _b = useState(true), canDispatchTransactions = _b[0], setCanDispatchTransactions = _b[1];
    useSsrLayoutEffect(function () {
        var state = createEditorState();
        var editorViewDOM = editorViewRef.current;
        if (editorViewDOM) {
            var pmEditorProps = createDirectEditorProps(state);
            var view = createEditorView(editorViewDOM, pmEditorProps);
            viewProvider.init(view);
            editorProps.onEditorReady && editorProps.onEditorReady(viewProvider);
        }
    }, []);
    function createEditorState() {
        var editorPlugins = createDefaultEditorPlugins();
        var config = processPluginsList(editorPlugins);
        var schema = createSchema(config);
        var plugins = createPMPlugins({
            schema: schema,
            editorConfig: config,
            portalProvider: portalProvider,
            pluginsProvider: pluginsProvider,
        });
        return EditorState.create({
            schema: schema,
            plugins: plugins,
        });
    }
    function createEditorView(element, editorProps) {
        var view = new EditorView({ mount: element }, editorProps);
        applyDevTools(view);
        return view;
    }
    function createDirectEditorProps(state) {
        return {
            state: state,
            dispatchTransaction: function (tr) {
                // Block stale transactions:
                // Prevent runtime exceptions from async transactions that would attempt to
                // update the DOM after React has unmounted the Editor.
                if (canDispatchTransactions) {
                    dispatchTransaction(tr);
                }
            },
            // Disables the contentEditable attribute of the editor if the editor is disabled
            editable: function (_state) { return !editorProps.disabled; },
            attributes: { 'data-gramm': 'false' },
        };
    }
    function dispatchTransaction(transaction) {
        var editorView = viewProvider.editorView;
        if (!editorView) {
            return;
        }
        var shouldTrack = editorProps.shouldTrack;
        analyticsProvider.perf.warn('EditorView', 'dispatchTransaction');
        var nodes = findChangedNodesFromTransaction(transaction);
        var changedNodesValid = validateNodes(nodes);
        if (changedNodesValid) {
            var oldEditorState = editorView.state;
            // go ahead and update the state now we know the transaction is good
            analyticsProvider.perf.info('EditorView', 'dispatchTransaction state::apply');
            var editorState = editorView.state.apply(transaction);
            analyticsProvider.perf.stop('EditorView', 'dispatchTransaction state::apply', 200);
            if (editorState === oldEditorState) {
                // I don't think it's possible for the React nodeviews to change without changing PM editorState but
                // it's better to be safe than sorry I guess.
                portalProvider.flush();
                return;
            }
            analyticsProvider.perf.warn('EditorView', 'dispatchTransaction updateState');
            editorView.updateState(editorState);
            analyticsProvider.perf.stop('EditorView', 'dispatchTransaction updateState', 100);
            analyticsProvider.perf.debug('EditorView', 'dispatchTransaction flush');
            portalProvider.flush();
            analyticsProvider.perf.stop('EditorView', 'dispatchTransaction flush', 0);
            editorProps.onDocumentEdit && editorProps.onDocumentEdit(editorView);
        }
        else {
            var invalidNodes = nodes
                .filter(function (node) { return !validNode(node); })
                .map(function (node) { return getDocStructure(node); });
            if (shouldTrack) {
                console.error('Invalid nodes in transaction');
                console.log(transaction);
                console.log(invalidNodes);
            }
        }
        analyticsProvider.perf.stop('EditorView', 'dispatchTransaction', 1000);
    }
    return (React.createElement(EditorLayoutComponent, null,
        React.createElement("div", { ref: editorViewRef })));
}

/**
 * Component to render the mounted nodeViews as portals.
 *
 * This allows them to share React context and it is a lot faster than using plain old ReactDOM.render
 * and unmountComponentAtNode. Previously I had a performance bottleneck with creating multiple nodeviews
 * at once with every update causing this PortalRenderer to re-render. Collecting those operations and running
 * a single flush function per updateState fixed this problem to large extent. This is, as far as I know, still
 * a problem in the original Atlassian editor so maybe they'll notice the same thing some day too.
 *
 * I discuss this here:
 * https://discuss.prosemirror.net/t/a-modified-version-of-atlassians-react-typescript-pm-editor/3441
 */
function PortalRenderer() {
    var portalProvider = useEditorContext().portalProvider;
    var _a = useReducer(function (x) { return x + 1; }, 0), _ignored = _a[0], forceUpdate = _a[1];
    var _b = useState(new Map()), portals = _b[0], setPortals = _b[1];
    useEffect(function () {
        portalProvider.addPortalRendererCallback(onUpdatePortals);
    }, []);
    var onUpdatePortals = function (newPortals) {
        setPortals(newPortals);
        forceUpdate();
    };
    return (React.createElement(React.Fragment, null, Array.from(portals.entries()).map(function (_a) {
        var container = _a[0], portal = _a[1];
        return portal;
    })));
}

function MarkButtonEl(props) {
    var className = props.className, active = props.active, name = props.name, icon = props.icon, onClick = props.onClick;
    return (React.createElement(Button, { className: className, active: active, name: name, onClick: onClick },
        React.createElement(SvgWrapper, null, icon)));
}
var SvgWrapper = styled.span(templateObject_1$1 || (templateObject_1$1 = __makeTemplateObject(["\n  display: flex;\n"], ["\n  display: flex;\n"])));
var Button = styled.button(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  background: ", ";\n  border: ", ";\n  cursor: pointer;\n  display: flex;\n  margin-right: 5px;\n  padding: 1px;\n  &:hover {\n    background: #f0f8ff;\n    opacity: 0.6;\n  }\n"], ["\n  background: ", ";\n  border: ", ";\n  cursor: pointer;\n  display: flex;\n  margin-right: 5px;\n  padding: 1px;\n  &:hover {\n    background: #f0f8ff;\n    opacity: 0.6;\n  }\n"])), function (props) { return props.active ? '#f0f8ff' : 'transparent'; }, function (props) { return props.active ? '1px solid #5a6ecd' : '1px solid transparent'; });
var MarkButton = styled(MarkButtonEl)(templateObject_3 || (templateObject_3 = __makeTemplateObject([""], [""])));
var templateObject_1$1, templateObject_2, templateObject_3;

// function markActive(state, type) {
//   let {from, $from, to, empty} = state.selection
//   if (empty) return type.isInSet(state.storedMarks || $from.marks())
//   else return state.doc.rangeHasMark(from, to, type)
// }
function Toolbar(props) {
    // Iterate over primaryToolbarComponents here
    var _a = useEditorContext(), viewProvider = _a.viewProvider, pluginsProvider = _a.pluginsProvider;
    var _b = useState([]), currentMarks = _b[0], setCurrentMarks = _b[1];
    useEffect(function () {
        pluginsProvider.subscribe(basePluginKey, onBasePluginChange);
        return function () {
            pluginsProvider.unsubscribe(basePluginKey, onBasePluginChange);
        };
    }, []);
    function onBasePluginChange(newPluginState) {
        setCurrentMarks(newPluginState.activeMarks);
    }
    function toggleBold() {
        viewProvider.execCommand(toggleMark(viewProvider.editorView.state.schema.marks.strong));
    }
    function toggleItalics() {
        viewProvider.execCommand(toggleMark(viewProvider.editorView.state.schema.marks.em));
    }
    return (React.createElement(Container, null,
        React.createElement(TopRow, null,
            React.createElement(MarkButton, { active: currentMarks.includes('strong'), name: "bold", icon: React.createElement(MdFormatBold, { size: 24 }), onClick: toggleBold }),
            React.createElement(MarkButton, { active: currentMarks.includes('em'), name: "italics", icon: React.createElement(MdFormatItalic, { size: 24 }), onClick: toggleItalics }))));
}
var Container = styled.div(templateObject_1$2 || (templateObject_1$2 = __makeTemplateObject(["\n  background: snow;\n  border: 1px solid black;\n  border-bottom: 0;\n  padding: 10px;\n"], ["\n  background: snow;\n  border: 1px solid black;\n  border-bottom: 0;\n  padding: 10px;\n"])));
var TopRow = styled.div(templateObject_2$1 || (templateObject_2$1 = __makeTemplateObject(["\n  display: flex;\n"], ["\n  display: flex;\n"])));
var templateObject_1$2, templateObject_2$1;

function FullPage(props) {
    var className = props.className, children = props.children;
    return (React.createElement(Container$1, { className: className },
        React.createElement(Toolbar, null),
        React.createElement(ContentArea, null, children)));
}
var Container$1 = styled.div(templateObject_1$3 || (templateObject_1$3 = __makeTemplateObject([""], [""])));
var ContentArea = styled.div(templateObject_2$2 || (templateObject_2$2 = __makeTemplateObject(["\nborder: 1px solid black;\n& > .ProseMirror {\n  min-height: 140px;\n  overflow-wrap: break-word;\n  outline: none;\n  padding: 10px;\n  white-space: pre-wrap;\n}\n\n.pm-blockquote {\n  box-sizing: border-box;\n  color: #2d82e1;\n  padding: 0 1em;\n  border-left: 4px solid #48a1fa;\n  margin: 0.2rem 0 0 0;\n  margin-right: 0;\n}\n"], ["\nborder: 1px solid black;\n& > .ProseMirror {\n  min-height: 140px;\n  overflow-wrap: break-word;\n  outline: none;\n  padding: 10px;\n  white-space: pre-wrap;\n}\n\n.pm-blockquote {\n  box-sizing: border-box;\n  color: #2d82e1;\n  padding: 0 1em;\n  border-left: 4px solid #48a1fa;\n  margin: 0.2rem 0 0 0;\n  margin-right: 0;\n}\n"])));
var templateObject_1$3, templateObject_2$2;

var components = {
    'full-page': FullPage,
};
function Editor(props) {
    var _a = props.appearance, appearance = _a === void 0 ? 'full-page' : _a;
    // These three have to be inside useMemos for SSR compatibility
    var viewProvider = useMemo(function () { return new EditorViewProvider; }, []);
    var portalProvider = useMemo(function () { return new PortalProvider; }, []);
    var pluginsProvider = useMemo(function () { return new PluginsProvider; }, []);
    var analyticsProvider = useMemo(function () { return new AnalyticsProvider(props.analytics); }, []);
    var Component = useMemo(function () { return components[appearance]; }, [appearance]);
    return (React.createElement(EditorContext.Provider, { value: {
            viewProvider: viewProvider,
            portalProvider: portalProvider,
            pluginsProvider: pluginsProvider,
            analyticsProvider: analyticsProvider
        } },
        React.createElement(ReactEditorView, { editorProps: props, EditorLayoutComponent: Component }),
        React.createElement(PortalRenderer, null)));
}
Editor.displayName = 'FullEditor';

export { Editor, EditorViewProvider };
